import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Message } from "@/entities/message";
import { useClipboard } from "use-clipboard-copy";
import { User } from "@/entities/user";
import { useMessageSelectionStore } from "./use-message-selection-store";

interface UseMessageSelectionProps {
  messagesRef: RefObject<Message[]>;
  user: User | null;
  onDeleteMessages?: (ids: string[]) => Promise<void>;
  onCopySuccess?: () => void;
}

export const useMessageSelection = ({
  messagesRef,
  user,
  onDeleteMessages,
  onCopySuccess,
}: UseMessageSelectionProps) => {
  const clipboard = useClipboard();

  const {
    selectedMessages,
    modeSelected,
    dragSelecting,
    isDragging,
    startDragPos,
    enterSelectionMode,
    exitSelectionMode,
    toggleMessage,
    selectMessages,
    deselectMessages,
    setDragSelecting,
    setIsDragging,
    setStartDragPos,
  } = useMessageSelectionStore();

  const handleMouseDown = useCallback(
    (idx: number, e: React.MouseEvent) => {
      if (e.button !== 0) {
        return;
      }

      if ((e.target as HTMLElement).closest("[data-is-draggable-file]")) {
        return;
      }

      const messages = messagesRef.current;
      if (!messages) return;

      const id = messages[idx]._id;
      const alreadySelected = selectedMessages.has(id);
      const mode: "select" | "deselect" = alreadySelected
        ? "deselect"
        : "select";

      setDragSelecting({ startIndex: idx, endIndex: idx, mode });
      setIsDragging(false);
      setStartDragPos({ x: e.clientX, y: e.clientY });
    },
    [
      messagesRef,
      selectedMessages,
      setDragSelecting,
      setIsDragging,
      setStartDragPos,
    ]
  );

  const handleMouseEnter = useCallback(
    (idx: number, e: React.MouseEvent) => {
      const messages = messagesRef.current;
      if (!dragSelecting || !messages || !startDragPos) return;

      const dy = Math.abs(e.clientY - startDragPos.y);

      if (!isDragging && dy < 3) return;

      const start = dragSelecting.startIndex;
      const end = idx;
      const [from, to] = [Math.min(start, end), Math.max(start, end)];
      const rangeMessages = messages.slice(from, to + 1);

      const validIdsInRange = rangeMessages
        .filter((m) => !m._id.startsWith("temp-"))
        .map((m) => m._id);

      if (validIdsInRange.length === 0) {
        return;
      }

      if (!modeSelected) {
        enterSelectionMode();
      }

      setIsDragging(true);

      if (dragSelecting.mode === "select") {
        selectMessages(validIdsInRange);
      } else {
        deselectMessages(validIdsInRange);
      }

      setDragSelecting({ ...dragSelecting, endIndex: idx });
    },
    [
      dragSelecting,
      startDragPos,
      isDragging,
      modeSelected,
      enterSelectionMode,
      selectMessages,
      deselectMessages,
      setDragSelecting,
      setIsDragging,
      messagesRef,
    ]
  );

  const endDragSelection = useCallback(() => {
    const { dragSelecting, isDragging, selectedMessages } =
      useMessageSelectionStore.getState();

    if (!dragSelecting) return;

    setDragSelecting(null);
    setIsDragging(false);
    setStartDragPos(null);

    if (isDragging && selectedMessages.size === 0) {
      exitSelectionMode();
    }
  }, [setDragSelecting, setIsDragging, setStartDragPos, exitSelectionMode]);

  const handleMouseUp = useCallback(
    (idx: number) => {
      const messages = messagesRef.current;
      if (!messages) {
        endDragSelection();
        return;
      }

      const message = messages[idx];
      const id = message._id;

      if (id.startsWith("temp-")) {
        endDragSelection();
        return;
      }

      if (!isDragging) {
        if (modeSelected) {
          const wasSelected = selectedMessages.has(id);
          const newSelectedSize = wasSelected
            ? selectedMessages.size - 1
            : selectedMessages.size + 1;

          toggleMessage(id);

          if (newSelectedSize === 0) {
            exitSelectionMode();
          }
        }
      }

      endDragSelection();
    },
    [
      isDragging,
      modeSelected,
      selectedMessages,
      toggleMessage,
      exitSelectionMode,
      messagesRef,
      endDragSelection,
    ]
  );

  useEffect(() => {
    if (dragSelecting) {
      const onWindowMouseUp = () => endDragSelection();
      window.addEventListener("mouseup", onWindowMouseUp);
      return () => window.removeEventListener("mouseup", onWindowMouseUp);
    }
  }, [dragSelecting, endDragSelection]);

  const copySelectedMessages = useCallback(() => {
    const messages = messagesRef.current;
    if (!selectedMessages.size || !messages || !user) return;

    const texts = Array.from(selectedMessages)
      .map((id) => messages.find((m) => m._id === id))
      .filter((m): m is Message => Boolean(m))
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
      .map((msg) => {
        const isMe = msg.owner._id === user._id;
        let name = isMe
          ? msg.owner.lastName
            ? `${msg.owner.firstName} ${msg.owner.lastName}`
            : msg.owner.firstName || "Я"
          : msg.chat.contact.lastName
          ? `${msg.chat.contact.firstName} ${msg.chat.contact.lastName}`
          : msg.chat.contact.firstName;

        const dateStr = new Date(msg.createdAt).toLocaleString("ru-RU", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });

        return `${name}, [${dateStr}]\n${msg.text || ""}`;
      });

    if (texts.length > 0) {
      clipboard.copy(texts.join("\n\n"));
      onCopySuccess?.();
    }
  }, [selectedMessages, user, clipboard, onCopySuccess]);

  const deleteSelectedMessages = useCallback(async () => {
    if (!selectedMessages.size || !onDeleteMessages) return;
    await onDeleteMessages(Array.from(selectedMessages));
    exitSelectionMode();
  }, [selectedMessages, onDeleteMessages, exitSelectionMode]);

  const resetSelection = exitSelectionMode;

  return useMemo(
    () => ({
      modeSelected,
      selectedMessages,
      isDragging,
      dragSelecting,
      handleMouseDown,
      handleMouseEnter,
      handleMouseUp,
      copySelectedMessages,
      resetSelection,
      deleteSelectedMessages,
    }),
    [
      modeSelected,
      selectedMessages,
      isDragging,
      dragSelecting,
      handleMouseDown,
      handleMouseEnter,
      handleMouseUp,
      copySelectedMessages,
      resetSelection,
      deleteSelectedMessages,
    ]
  );
};
